import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http, formatTRY, formatNumber } from "@/lib/api";
import { useFilters } from "@/state/FilterContext";
import { Loader2 } from "lucide-react";

/**
 * Drill-down modal showing shipments filtered by:
 * - global filters (FilterContext)
 * - plus per-click overrides (location, carrier, mode)
 */
export default function DrillDownModal({ open, onOpenChange, title, overrides = {} }) {
  const { queryParams } = useFilters();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = { ...queryParams(), ...overrides, limit: 100 };
    http
      .get("/dashboard/shipments", { params })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [open, JSON.stringify(overrides)]);

  const total = rows.reduce((s, r) => s + r.cost_try, 0);
  const tons = rows.reduce((s, r) => s + r.weight_tons, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="drilldown-modal" className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">{title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Filtrelenmiş sevkiyat kayıtları
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 mt-2">
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">Kayıt</div>
            <div className="font-display text-lg font-bold text-slate-900 mt-1 tabular-nums">{formatNumber(rows.length)}</div>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">Toplam Maliyet</div>
            <div className="font-display text-lg font-bold text-slate-900 mt-1 tabular-nums">{formatTRY(total)}</div>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">Toplam Ağırlık</div>
            <div className="font-display text-lg font-bold text-slate-900 mt-1 tabular-nums">{formatNumber(tons)} t</div>
          </div>
        </div>

        <div className="overflow-auto flex-1 mt-3 border border-slate-200 rounded-md">
          {loading ? (
            <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Bu kombinasyona ait kayıt yok.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">
                  <th className="p-2.5 pl-3">TM ID</th>
                  <th className="p-2.5">Tarih</th>
                  <th className="p-2.5">Lokasyon</th>
                  <th className="p-2.5">Nakliyeci</th>
                  <th className="p-2.5">Mod</th>
                  <th className="p-2.5 text-right">Ağırlık</th>
                  <th className="p-2.5 text-right pr-3">Maliyet</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} data-testid={`drilldown-row-${i}`} className="border-t border-slate-100">
                    <td className="p-2.5 pl-3 font-mono text-xs text-slate-700">{r.id}</td>
                    <td className="p-2.5 text-slate-600">{new Date(r.date).toLocaleDateString("tr-TR")}</td>
                    <td className="p-2.5 text-slate-800 font-medium">{r.source_name}</td>
                    <td className="p-2.5 text-slate-700">{r.carrier_name}</td>
                    <td className="p-2.5 text-slate-600">{r.mode}</td>
                    <td className="p-2.5 text-right tabular-nums text-slate-700">{formatNumber(r.weight_tons)} t</td>
                    <td className="p-2.5 pr-3 text-right tabular-nums font-semibold text-slate-900">{formatTRY(r.cost_try)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
